import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRightOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Image,
  Menu,
  Row,
  Space,
  Spin,
  Typography,
} from 'antd';
import type { CollapseProps, MenuProps } from 'antd';
import {
  FaqAnswerBlock,
  fetchPublishedFaqs,
  ImgCloudFaq,
  readFaqBootstrapData,
} from '../../services/faq';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import './index.css';

const { Link, Paragraph, Text, Title } = Typography;
const ALL_CATEGORIES = '全部';

const AnswerBlocks: React.FC<{ blocks: FaqAnswerBlock[] }> = ({ blocks }) => (
  <div className="faq-answer">
    {blocks.map((block, index) => {
      const key = `${block.type}-${index}`;
      if (block.type === 'heading') return <Title level={5} key={key}>{block.text}</Title>;
      if (block.type === 'paragraph') return <Paragraph key={key}>{block.text}</Paragraph>;
      if (block.type === 'list') {
        const items = block.items || [];
        return block.style === 'ordered'
          ? <ol key={key}>{items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`}>{item}</li>)}</ol>
          : <ul key={key}>{items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`}>{item}</li>)}</ul>;
      }
      if (block.type === 'image') {
        return (
          <figure key={key}>
            <Image src={block.url} alt={block.alt || ''} preview />
            {block.caption && <figcaption><Text type="secondary">{block.caption}</Text></figcaption>}
          </figure>
        );
      }
      return (
        <Paragraph key={key}>
          <Link href={block.url} target="_blank" rel="noopener noreferrer">
            {block.text} <ArrowRightOutlined aria-hidden="true" />
          </Link>
        </Paragraph>
      );
    })}
  </div>
);

const Faq: React.FC = () => {
  const supportEmail = useSupportEmail();
  const [faqs, setFaqs] = useState<ImgCloudFaq[]>(() => readFaqBootstrapData());
  const [loading, setLoading] = useState(faqs.length === 0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    if (faqs.length === 0) setLoading(true);
    fetchPublishedFaqs()
      .then((currentFaqs) => {
        if (!active) return;
        setFaqs(currentFaqs);
        setLoadFailed(false);
      })
      .catch((error) => {
        if (!active) return;
        console.warn('Failed to refresh FAQ content', error);
        setLoadFailed(true);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // reloadKey intentionally triggers the explicit retry action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    faqs.forEach((faq) => counts.set(faq.category, (counts.get(faq.category) || 0) + 1));
    return [{ name: ALL_CATEGORIES, count: faqs.length }, ...Array.from(counts, ([name, count]) => ({ name, count }))];
  }, [faqs]);

  const filteredFaqs = useMemo(
    () => faqs.filter((faq) => activeCategory === ALL_CATEGORIES || faq.category === activeCategory),
    [activeCategory, faqs],
  );

  useEffect(() => {
    if (activeCategory !== ALL_CATEGORIES && !categories.some((category) => category.name === activeCategory)) {
      setActiveCategory(ALL_CATEGORIES);
    }
  }, [activeCategory, categories]);

  const groupedFaqs = useMemo(() => {
    const groups = new Map<string, ImgCloudFaq[]>();
    filteredFaqs.forEach((faq) => groups.set(faq.category, [...(groups.get(faq.category) || []), faq]));
    return Array.from(groups, ([category, items]) => ({ category, items }));
  }, [filteredFaqs]);

  const categoryItems: MenuProps['items'] = categories.map((category) => ({
    key: category.name,
    label: (
      <span className="faq-category-label">
        <span>{category.name}</span>
        <Text type="secondary">{category.count}</Text>
      </span>
    ),
  }));

  return (
    <div id="faq-page" className="faq-page">
      <Card className="faq-main-card">
        <section id="faq-list" aria-label="常见问题列表">
          <Row gutter={[24, 24]}>
            <Col xs={24} md={7} lg={6}>
              <Space direction="vertical" size="middle" className="faq-sidebar">
                <Card size="small" title="问题分类" className="faq-category-card">
                  <Menu
                    mode="inline"
                    className="faq-category-menu"
                    items={categoryItems}
                    selectedKeys={[activeCategory]}
                    onClick={({ key }) => setActiveCategory(key)}
                  />
                </Card>
                <Card size="small" title="没有找到答案？" className="faq-support-card">
                  <Space direction="vertical" size="small">
                    <Text type="secondary">可以通过邮件告诉我们你的问题。</Text>
                    <Button
                      type="link"
                      icon={<MailOutlined />}
                      href={`mailto:${supportEmail}`}
                      className="faq-support-link"
                    >
                      联系我们
                    </Button>
                  </Space>
                </Card>
              </Space>
            </Col>

            <Col xs={24} md={17} lg={18}>
              {loadFailed && faqs.length > 0 && (
                <Alert
                  className="faq-stale-note"
                  type="warning"
                  showIcon
                  message="实时内容暂时无法更新，当前展示构建时保存的版本。"
                />
              )}

              {loading && faqs.length === 0 ? (
                <div className="faq-state"><Spin size="large" /><Text type="secondary">正在加载常见问题...</Text></div>
              ) : groupedFaqs.length > 0 ? (
                groupedFaqs.map((group, groupIndex) => {
                  const headingId = `faq-category-${groupIndex}`;
                  const collapseItems: CollapseProps['items'] = group.items.map((faq) => ({
                    key: faq.id,
                    label: faq.question,
                    children: <AnswerBlocks blocks={faq.answer.blocks} />,
                    forceRender: true,
                  }));
                  return (
                    <section className="faq-group" key={group.category} aria-labelledby={headingId}>
                      <div className="faq-group-header">
                        <Title level={4} id={headingId}>{group.category}</Title>
                        <Text type="secondary">{group.items.length} 个问题</Text>
                      </div>
                      <Collapse items={collapseItems} expandIconPosition="end" />
                    </section>
                  );
                })
              ) : (
                <Empty
                  className="faq-empty-state"
                  description={loadFailed ? '常见问题暂时无法加载' : '常见问题正在整理中'}
                >
                  {loadFailed && (
                    <Button icon={<ReloadOutlined />} onClick={() => setReloadKey((key) => key + 1)}>
                      重新加载
                    </Button>
                  )}
                </Empty>
              )}
            </Col>
          </Row>
        </section>
      </Card>
    </div>
  );
};

export default Faq;
